"use client";

import { useLocale } from "@/components/LocaleProvider";

const COMPANY_URLS: (string | undefined)[] = [
  "https://citrii.com",
  "https://turfsport.co.za",
  "https://pamgolding.co.za",
];

const EXPERIENCE_INDICES = [0, 1, 2] as const;
const BULLET_COUNTS: Record<number, number> = { 0: 4, 1: 3, 2: 3 };

export default function Experience() {
  const { t } = useLocale();

  return (
    <section
      id="experience"
      className="scroll-mt-12 pt-14 pb-14 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-[3em]"
    >
      <h2 className="inline-block text-sm font-bold font-montserrat uppercase bg-[#333333] text-white dark:bg-white dark:text-black px-3 py-1.5 mb-12">
        {t("sections.experience")}
      </h2>
      <div className="space-y-16">
        {EXPERIENCE_INDICES.map((index) => {
          const companyUrl = COMPANY_URLS[index];
          const bulletCount = BULLET_COUNTS[index];
          const techList = t(`experience.${index}.technologies`).split(", ");
          return (
            <div
              key={index}
              className="flex flex-col md:flex-row gap-8 group rounded-lg p-4 -m-4 border border-transparent transition-all duration-200"
            >
              <div className="md:w-32 flex-shrink-0 flex flex-col gap-2">
                <span className="text-sm font-normal text-black/60 dark:text-white/60 group-hover:text-black/70 dark:group-hover:text-white/70 transition-colors">
                  {t(`experience.${index}.period`).toUpperCase()}
                </span>
                <span className="inline-block w-fit rounded px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide bg-neutral-100 text-neutral-700 dark:bg-neutral-500 dark:text-neutral-100">
                  {t(`experience.${index}.sector`)}
                </span>
              </div>
              <div className="flex-1 flex flex-col">
                <div className="order-1 mb-3">
                  <div>
                    <h3 className="text-lg font-bold text-black dark:text-white group-hover:text-black/80 dark:group-hover:text-white/80 transition-colors">
                      {t(`experience.${index}.title`)}
                      {" · "}
                      {companyUrl ? (
                        <a
                          href={companyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-bold text-black dark:text-white group-hover:text-black/80 dark:group-hover:text-white/80 transition-colors inline-flex items-center gap-1.5 focus-visible:outline-2 focus-visible:outline-black dark:focus-visible:outline-white focus-visible:outline-offset-2 rounded"
                          aria-label={`${t(`experience.${index}.company`)} - ${t("common.opensInNewWindow")}`}
                        >
                          {t(`experience.${index}.company`)}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="group-hover:-translate-y-0.5 transition-transform duration-200"
                          >
                            <path d="M7 17L17 7M7 7h10v10" />
                          </svg>
                        </a>
                      ) : (
                        <span className="font-bold">{t(`experience.${index}.company`)}</span>
                      )}
                    </h3>
                    {index === 0 && (
                      <div className="mt-0.5">
                        <p
                          className="text-[15px] font-normal text-black/60 dark:text-white/60 group-hover:text-black/70 dark:group-hover:text-white/70 transition-colors"
                        >
                          {t("sections.prev")}: {t("experience.0.roleChanges0")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <ul className="order-2 md:order-4 flex flex-wrap gap-2 mb-6 md:mb-0 md:mt-10">
                  {techList.map((tech, techIndex) => (
                    <li
                      key={techIndex}
                      className="px-3 py-1 text-xs rounded-full border border-black/15 dark:border-white/20 max-md:bg-white max-md:text-neutral-800 md:bg-black/5 md:text-black/70 dark:max-md:bg-white dark:max-md:text-neutral-900 dark:md:bg-white/5 dark:md:text-white/70"
                    >
                      {tech.trim()}
                    </li>
                  ))}
                </ul>
                <p className="order-3 md:order-2 text-[15px] text-black/70 dark:text-white/70 mb-4 md:mb-5 leading-relaxed">
                  {t(`experience.${index}.description`)}
                </p>
                {bulletCount > 0 && (
                  <ul className="order-4 md:order-3 text-[15px] text-black/70 dark:text-white/70 mb-4 md:mb-0 leading-relaxed list-disc list-outside space-y-1 ml-6 pl-0">
                    {Array.from({ length: bulletCount }, (_, i) => (
                      <li key={i}>{t(`experience.${index}.bullet${i + 1}`)}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
